import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import moment from 'moment';
import { formatarData } from 'src/app/core/utils/utilities';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { EstoqueService } from 'src/app/services/EstoqueService/estoque.service';
import {
  IEstoqueRelatorioFilter,
  IEstoqueRelatorioMovimentacaoListResponse
} from 'src/app/services/EstoqueService/estoque.model';
import { IProduto } from 'src/app/services/ProdutoService/produto.model';
import { ProdutoService } from 'src/app/services/ProdutoService/produto.service';
import { labelOrigemMovimentacao, labelTipoMovimentacao } from '../../estoque.constants';

type IOptions = { name: string; value: number };

@Component({
  selector: 'app-relatorio-estoque-movimentacoes',
  templateUrl: './relatorio-estoque-movimentacoes.component.html',
  styleUrls: ['./relatorio-estoque-movimentacoes.component.scss']
})
export class RelatorioEstoqueMovimentacoesComponent implements OnInit, AfterViewInit {
  form = this.fb.group({
    DT_INIC: [null as string | null],
    DT_FIM: [null as string | null],
    FK_PROD: [0]
  });

  isLoading = false;
  tableData: Array<any> = [];
  produtoOptions: IOptions[] = [{ name: 'Todos', value: 0 }];

  tableColumns = [
    { header: 'Data', key: 'data', type: 'text' },
    { header: 'Produto', key: 'produto', type: 'text' },
    { header: 'Tipo', key: 'tipo', type: 'text' },
    { header: 'Origem', key: 'origem', type: 'text' },
    { header: 'Saldo Ant.', key: 'saldoAnt', type: 'text' },
    { header: 'Quantidade', key: 'quantidade', type: 'text' },
    { header: 'Saldo Após', key: 'saldoApos', type: 'text' },
    { header: 'Usuário', key: 'usuario', type: 'text' }
  ];

  constructor(
    private fb: FormBuilder,
    private estoqueService: EstoqueService,
    private produtoService: ProdutoService,
    private appExportBase64: AppExportBase64
  ) {
    const fim = moment().endOf('day');
    const inicio = moment().subtract(3, 'months').startOf('month');
    this.form.patchValue({
      DT_INIC: inicio.format('YYYY-MM-DDTHH:mm'),
      DT_FIM: fim.format('YYYY-MM-DDTHH:mm')
    });
  }

  ngOnInit(): void {
    this.loadProdutos();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => this.loadData());
  }

  loadProdutos(): void {
    this.produtoService.GetProdutos().subscribe({
      next: (produtos: IProduto[]) => {
        this.produtoOptions = [
          { name: 'Todos', value: 0 },
          ...produtos.map(p => ({ name: p.NM_PROD, value: p.PK_PROD }))
        ];
      }
    });
  }

  onSelectChange(event: number, name: 'FK_PROD'): void {
    this.form.controls[name].setValue(event);
  }

  private createFilter(): IEstoqueRelatorioFilter {
    const filter: IEstoqueRelatorioFilter = {
      DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
      DT_FIM: this.form.controls.DT_FIM.value ?? undefined
    };
    const fkProd = Number(this.form.controls.FK_PROD.value);
    if (fkProd > 0) filter.FK_PROD = fkProd;
    return filter;
  }

  loadData(): void {
    this.isLoading = true;
    this.estoqueService.GetRelatorioMovimentacoes(this.createFilter()).subscribe({
      next: (response: IEstoqueRelatorioMovimentacaoListResponse) => {
        const items = response.ITEMS ?? [];
        this.tableData = items.map(item => ({
          produto: item.NM_PROD,
          tipo: labelTipoMovimentacao(item.NU_TIPO_MOVM),
          origem: labelOrigemMovimentacao(item.NU_ORIG_MOVM),
          quantidade: item.QT_MOVM,
          saldoAnt: item.QT_SALD_ANT,
          saldoApos: item.QT_SALD_APOS,
          usuario: item.NM_FUNC_OPER ?? '---',
          data: formatarData(item.DT_MOVM)
        }));
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarExcel(): void {
    this.isLoading = true;
    const filter = this.createFilter();
    this.estoqueService
      .GetReportExcel({
        ReportType: 'movimentacoes',
        DT_INIC: filter.DT_INIC,
        DT_FIM: filter.DT_FIM,
        FK_PROD: filter.FK_PROD
      })
      .subscribe({
        next: response => {
          this.appExportBase64.downloadExcel(response.ReportBase64, 'movimentacoes-estoque');
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }
}
